import { useEffect, useState } from 'react'
import { Users, FileText, Users as GroupsIcon, TrendingUp, Loader } from 'lucide-react'
import { getTotalUsersCount } from '../lib/userService'
import { listingService } from '../lib/listingService'
import { groupService } from '../lib/groupService'

// Configuração dos stats - agora com valores reais que serão carregados
const statConfigs = [
  {
    id: 'members',
    icon: <Users className="w-8 h-8 md:w-10 md:h-10" />,
    label: 'Membros Ativos',
    suffix: 'K+',
    description: 'Brasileiros conectados na plataforma',
    formatValue: (value: number) => {
      if (value >= 1000) return `${(value / 1000).toFixed(0)}K+`
      return `${value}+`
    },
    getValue: async () => {
      const count = await getTotalUsersCount()
      return count
    },
  },
  {
    id: 'listings',
    icon: <FileText className="w-8 h-8 md:w-10 md:h-10" />,
    label: 'Publicações',
    suffix: '+',
    description: 'Anúncios ativos no marketplace',
    formatValue: (value: number) => `${value.toLocaleString('pt-BR')}+`,
    getValue: async () => {
      // Soma de propriedades, carros e serviços ativos
      const [properties, cars, services] = await Promise.all([
        listingService.getActivePropertiesCount(),
        listingService.getActiveCarsCount(),
        listingService.getActiveServicesCount(),
      ])
      return properties + cars + services
    },
  },
  {
    id: 'groups',
    icon: <GroupsIcon className="w-8 h-8 md:w-10 md:h-10" />,
    label: 'Grupos',
    suffix: '+',
    description: 'Comunidades temáticas ativas',
    formatValue: (value: number) => `${value}+`,
    getValue: async () => {
      const groups = await groupService.getGroups({})
      return groups.length
    },
  },
  {
    id: 'help',
    icon: <TrendingUp className="w-8 h-8 md:w-10 md:h-10" />,
    label: 'Pessoas Ajudadas',
    suffix: 'M+',
    description: 'Mais de 1 milhão de pessoas assistidas',
    formatValue: (value: number) => `${value.toFixed(1).replace('.', ',')}M+`,
    getValue: async () => {
      // Valor fixo de 1.2 milhões (pode ser ajustado posteriormente)
      return 1.2
    },
  },
]

const StatsCounter = () => {
  const [realValues, setRealValues] = useState<Record<string, number>>({})
  const [animatedValues, setAnimatedValues] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)

  // Carregar dados reais
  useEffect(() => {
    const loadRealData = async () => {
      setLoading(true)
      try {
        const values: Record<string, number> = {}
        
        // Carregar todos os dados em paralelo
        const promises = statConfigs.map(async (stat) => {
          try {
            const value = await stat.getValue()
            values[stat.id] = value
          } catch (error) {
            console.error(`Erro ao carregar valor para ${stat.id}:`, error)
            values[stat.id] = 0
          }
        })
        
        await Promise.all(promises)
        setRealValues(values)
        
        // Iniciar animação dos valores
        const intervals: NodeJS.Timeout[] = []
        statConfigs.forEach((stat) => {
          const targetValue = values[stat.id] || 0
          const duration = 2000 // 2 segundos para animação
          const step = targetValue / (duration / 16) // 60fps approx
          let current = 0
          
          const interval = setInterval(() => {
            current += step
            if (current >= targetValue) {
              current = targetValue
              clearInterval(interval)
            }
            setAnimatedValues(prev => ({ ...prev, [stat.id]: current }))
          }, 16)

          intervals.push(interval)
        })
        
        return () => intervals.forEach(interval => clearInterval(interval))
      } catch (error) {
        console.error('Erro ao carregar dados:', error)
      } finally {
        setLoading(false)
      }
    }
    
    loadRealData()
  }, [])

  const formatDisplayValue = (statId: string, value: number) => {
    const stat = statConfigs.find(s => s.id === statId)
    if (!stat) return `${value}`
    
    if (stat.id === 'members') {
      // Para membros, converter para K+ se for maior que 1000
      if (value >= 1000) {
        return `${(value / 1000).toFixed(0)}K+`
      }
      return `${value}+`
    }
    
    if (stat.id === 'listings') {
      return `${value.toLocaleString('pt-BR')}+`
    }
    
    if (stat.id === 'help') {
      return `${value.toFixed(1).replace('.', ',')}M+`
    }
    
    // Para grupos e outros
    return `${value}+`
  }

  const getMaxValue = (statId: string) => {
    const realValue = realValues[statId] || 0
    // Para a barra de progresso, usamos o valor real como máximo
    return realValue
  }

  return (
    <div className="w-full py-12 md:py-20 bg-gradient-to-b from-noe-900 to-noe-950 rounded-2xl border border-noe-800 shadow-2xl">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
           <h2 className="text-3xl md:text-4xl font-bold text-noe-100 mb-4">
             Nossos Números em <span className="text-noe-brand">Tempo Real</span>
           </h2>
          <p className="text-lg text-noe-300 max-w-3xl mx-auto">
            Acompanhe o crescimento da maior comunidade de brasileiros nos Estados Unidos
          </p>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader className="w-12 h-12 text-noe-brand animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {statConfigs.map((stat) => {
                const animatedValue = animatedValues[stat.id] || 0
                const maxValue = getMaxValue(stat.id)
                const percentage = maxValue > 0 ? (animatedValue / maxValue) * 100 : 0
                
                return (
                  <div
                    key={stat.id}
                    className="bg-noe-800/30 backdrop-blur-sm border border-noe-700/50 rounded-2xl p-6 md:p-8 flex flex-col items-center text-center hover:border-noe-brand/50 hover:bg-noe-800/50 transition-all duration-300 group"
                  >
                    <div className="mb-4 p-4 rounded-full bg-noe-brand/10 text-noe-brand group-hover:scale-110 transition-transform duration-300">
                      {stat.icon}
                    </div>
                    
                    <div className="text-4xl md:text-5xl font-bold text-noe-100 mb-2">
                      {formatDisplayValue(stat.id, Math.floor(animatedValue))}
                    </div>
                    
                    <h3 className="text-xl font-semibold text-noe-100 mb-2">
                      {stat.label}
                    </h3>
                    
                    <p className="text-noe-300 text-sm md:text-base">
                      {stat.description}
                    </p>
                    
                    <div className="mt-6 w-full">
                      <div className="h-1.5 bg-noe-700 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-noe-brand to-noe-brand/70 rounded-full transition-all duration-1000"
                          style={{ 
                            width: `${percentage}%` 
                          }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-noe-400 mt-2">
                        <span>0</span>
                        <span>{formatDisplayValue(stat.id, maxValue)}</span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            
            <div className="text-center mt-12 pt-8 border-t border-noe-800/50">
              <p className="text-noe-400 text-sm md:text-base">
                * Dados atualizados em tempo real. Nosso compromisso é conectar cada vez mais brasileiros.
              </p>
              <p className="text-noe-brand font-medium mt-4 text-lg">
                Participe você também da nossa comunidade!
              </p>
              <p className="text-noe-300 mt-2">
                Junte-se a milhares de brasileiros que já estão conectados, informados e crescendo juntos.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default StatsCounter